"use client"

import { useEffect, useRef, useState, useCallback } from "react"
import { X, ChevronLeft, ChevronRight, Images } from "lucide-react"
import { useTranslations, useLocale } from 'next-intl'

// Static gallery data with placeholder images
const galleryItems = [
  { id: 1, title: "Sidi Bou Said", category: "Architecture", image: "/sidi-bou-said.jpg" },
  { id: 2, title: "El Jem Amphitheater", category: "History", image: "el-jem.jpg" },
  { id: 3, title: "Medina Market", category: "Culture", image: "/medina-market.jpg" },
  { id: 4, title: "Sahara Desert", category: "Landscape", image: "/sahara-desert.jpg" },
  { id: 5, title: "Ceramic Tiles", category: "Art", image: "/ceramic-tiles.jpg" },
  { id: 6, title: "Carthage Ruins", category: "History", image: "/carthage-ruins.jpg" },
  { id: 7, title: "Traditional Door", category: "Architecture", image: "/tunisian-door.jpg" },
  { id: 8, title: "Mediterranean Coast", category: "Nature", image: "/mediterranean-coast.jpg" },
]

export default function GallerySection() {
  const t = useTranslations()
  const locale = useLocale()
  
  const [openLightbox, setOpenLightbox] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showAll, setShowAll] = useState(false)

  const displayedItems = showAll ? galleryItems : galleryItems.slice(0, 5)

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!openLightbox) return
    
    switch (e.key) {
      case 'Escape':
        setOpenLightbox(false)
        break
      case 'ArrowLeft':
        e.preventDefault()
        setCurrentIndex((prev) => (prev - 1 + galleryItems.length) % galleryItems.length)
        break
      case 'ArrowRight':
        e.preventDefault()
        setCurrentIndex((prev) => (prev + 1) % galleryItems.length)
        break
    }
  }, [openLightbox])

  // Add event listeners
  useEffect(() => {
    if (openLightbox) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
      
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
        document.body.style.overflow = ''
      }
    }
  }, [openLightbox, handleKeyDown])

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % galleryItems.length)
  }

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + galleryItems.length) % galleryItems.length)
  }

  const openLightboxAtIndex = (index: number) => {
    setCurrentIndex(index)
    setOpenLightbox(true)
  }

  return (
    <>
      <section className="w-full py-16 md:py-24 bg-background relative overflow-hidden" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="mb-10 md:mb-14">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t('common.photo')} <span className="text-primary">{t('common.gallery')}</span>
            </h2>
            <p className="text-muted-foreground text-base md:text-lg max-w-2xl">
              {t('sections.historicalSites')}
            </p>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4 lg:gap-6">
            {/* Featured large image */}
            {displayedItems.length > 0 && (
              <div
                className="relative cursor-pointer overflow-hidden rounded-lg md:rounded-xl group shadow-lg hover:shadow-xl transition-shadow duration-300 col-span-2 row-span-2"
                onClick={() => openLightboxAtIndex(0)}
              >
                <img
                  src={displayedItems[0].image}
                  alt={displayedItems[0].title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Overlay with view button */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
                  <button className="bg-white text-foreground px-6 py-3 rounded-lg font-semibold text-sm md:text-base flex items-center gap-2 hover:bg-white/95 transition-all transform hover:scale-105 shadow-xl">
                    <Images className="w-5 h-5" />
                    View Gallery
                  </button>
                </div>
                
                {/* Image info */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                  <p className="text-sm text-white/70 font-medium">{displayedItems[0].category}</p>
                  <h3 className="text-lg md:text-xl font-semibold text-white">{displayedItems[0].title}</h3>
                </div>
              </div>
            )}

            {/* Other gallery items */}
            {displayedItems.slice(1).map((item, index) => (
              <div
                key={item.id}
                onClick={() => openLightboxAtIndex(index + 1)}
                className="relative cursor-pointer overflow-hidden rounded-lg md:rounded-xl group shadow-md hover:shadow-xl transition-shadow duration-300 min-h-[120px] sm:min-h-[150px]"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300" />
                
                {/* Image info on hover */}
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-xs text-white/70">{item.category}</p>
                  <h3 className="text-sm font-medium text-white">{item.title}</h3>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Lightbox Modal */}
      {openLightbox && (
        <div className="fixed inset-0 bg-black z-[999] flex flex-col">
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 p-4 md:p-6 flex justify-between items-center z-10 bg-gradient-to-b from-black/70 to-transparent">
            <div className="text-white text-sm md:text-base font-medium">
              {currentIndex + 1} / {galleryItems.length}
            </div>
            <div className="text-center flex-1">
              <h3 className="text-white text-lg md:text-xl font-semibold">{galleryItems[currentIndex].title}</h3>
              <p className="text-white/70 text-sm">{galleryItems[currentIndex].category}</p>
            </div>
            <button
              className="text-white hover:bg-white/10 rounded-full p-2 transition"
              onClick={() => setOpenLightbox(false)}
            >
              <X className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>

          {/* Main Image */}
          <div className="flex-1 flex items-center justify-center p-8 md:p-20">
            <div className="relative max-w-full max-h-full">
              <img
                src={galleryItems[currentIndex]?.image || "/placeholder.svg"}
                alt={galleryItems[currentIndex]?.title || "Gallery image"}
                className="max-h-[60vh] md:max-h-[70vh] max-w-full object-contain rounded-lg"
              />
            </div>
          </div>

          {/* Navigation buttons */}
          <button
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-2 md:p-3 transition"
            onClick={prevImage}
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          <button
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-2 md:p-3 transition"
            onClick={nextImage}
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </div>
      )}
    </>
  )
}